import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CarouselComponent } from '../../../shared/components/primeng/carousel/carousel.component';
import { FeatureCardComponent, FeatureCardData } from '../../../shared/components/feature-card/feature-card.component';
import { ModelCardComponent, ModelCardData } from '../../../shared/components/model-card/model-card.component';
import { CatalogueApiService } from '@angular-workspace/services';
import { extractDatasetResourceDisplayTags } from '@angular-workspace/feature';
import { FormatService } from '../../../shared/services/format.service';
import { HelperService } from '../../../shared/services/helper.service';

export interface FeatureTab {
  id: string;
  label: string;
  description: string;
  viewAllLink: string;
  viewAllText: string;
  assetType: string; // Asset type for API call
}

@Component({
  selector: 'app-features',
  standalone: true,
  imports: [CommonModule, CarouselComponent, FeatureCardComponent, ModelCardComponent],
  templateUrl: './features.component.html',
  styleUrl: './features.component.scss',
})
export class FeaturesComponent implements OnInit, OnDestroy {
  activeTab = 'datasets';
  isLoading = false;
  itemsToShow = 3; // Default: 3 cards for desktop

  constructor(
    private catalogueApiService: CatalogueApiService,
    private formatService: FormatService,
    private helperService: HelperService,
    private router: Router
  ) {
    this.updateItemsToShow();
  }

  tabs: FeatureTab[] = [
    {
      id: 'datasets',
      label: 'Datasets',
      description: 'Access comprehensive agricultural datasets—spanning crop patterns, soil health, weather analytics, and market dynamics—to build and validate high-impact AI solutions.',
      viewAllLink: '/datasets',
      viewAllText: 'View all Datasets',
      assetType: 'Dataset'
    },
    {
      id: 'ai-models',
      label: 'Models',
      description: 'Explore pre-trained Models ready for deployment in various concepts and Use Cases.',
      viewAllLink: '/models',
      viewAllText: 'View all Models',
      assetType: 'Model'
    }
  ];

  datasetsCards: FeatureCardData[] = [
    {
      id: '1',
      title: 'IMD wildlife & livestock data for Maharashtra State',
      shortDescription: 'Wildlife and livestock datasets from IMD for Maharashtra State for analysis and planning.',
      organization: 'Agromet Advisory Service Division, IMD',
      tags: ['FEATURE', 'OGC', 'FILE'],
      percentage: 95.77,
      fileType: 'CSV',
      date: 'Jun 22, 2025',
      badge: 'Public',
      badgeColor: 'public',
      resourceCount: 2,
      link: '/datasets/1',
      metrics: { likes: 0, views: 0, downloads: 0 },
    },
    {
      id: '3',
      title: 'RFD Agri cover advisory for Maharashtra State',
      shortDescription: 'RFD agricultural cover advisory data for Maharashtra State.',
      organization: 'Agromet Advisory Service Division, IMD',
      tags: ['STAC', 'OGC', 'FILE'],
      percentage: 95.77,
      fileType: 'CSV',
      date: 'Jun 22, 2025',
      badge: 'Public',
      badgeColor: 'public',
      isNew: true,
      link: '/datasets/3',
      metrics: { likes: 0, views: 0, downloads: 0 },
    },
    {
      id: '4',
      title: 'Soil Health Card Data',
      shortDescription: 'Soil health card metrics and indicators for agricultural assessment.',
      organization: 'FEAT - Krishi Dept',
      tags: ['FEATURE', 'FILE'],
      percentage: 94,
      fileType: 'CSV',
      date: 'Jun 22, 2025',
      badge: 'Public',
      badgeColor: 'public',
      link: '/datasets/4',
      metrics: { likes: 0, views: 0, downloads: 0 },
    },
    {
      id: '2',
      title: 'District-wise yearly Rain-water Info in Maharashtra',
      shortDescription: 'Yearly rainwater and precipitation information at district level in Maharashtra.',
      organization: 'Maharashtra Water Works',
      tags: ['STAC', 'OGC', 'FILE'],
      percentage: 95.77,
      fileType: 'CSV',
      date: 'Jun 22, 2025',
      badge: 'Public',
      badgeColor: 'public',
      link: '/datasets/2',
      metrics: { likes: 0, views: 0, downloads: 0 },
    },
  ];

  aiModelsCards: ModelCardData[] = [
    {
      id: '5',
      title: 'Invasive Species Prediction & Detection',
      organization: 'Maharashtra Agri Department',
      industry: 'Sector Agnostic',
      tags: ['Smart Agri', 'AI Agri', 'Habitat planning'],
      modelType: 'Text classification model',
      language: 'Python',
      date: 'Dec, 2024',
      badge: 'Public',
      badgeColor: 'public',
      link: '/model/5',
      metrics: { likes: 0, views: 0, downloads: 0 },
    },
    {
      id: '6',
      title: 'Crop Disease Detection Model',
      organization: 'AI Research Lab, Maharashtra',
      industry: 'Agriculture',
      tags: ['TensorFlow', 'PyTorch', 'Model'],
      modelType: 'Image classification model',
      language: 'Python',
      date: 'Dec, 2024',
      badge: 'Public',
      badgeColor: 'public',
      link: '/model/6',
      metrics: { likes: 0, views: 0, downloads: 0 },
    },
    {
      id: '7',
      title: 'Traffic Flow Prediction',
      organization: 'Smart City Initiative',
      industry: 'Transport',
      tags: ['Keras', 'LSTM', 'Model'],
      modelType: 'Time series model',
      language: 'Python',
      date: 'Nov, 2024',
      badge: 'Public',
      badgeColor: 'public',
      link: '/model/7',
      metrics: { likes: 0, views: 0, downloads: 0 },
    },
  ];

  currentCards: (FeatureCardData | ModelCardData)[] = [];
  currentTab: FeatureTab = this.tabs[0];

  // Cache for loaded data to avoid redundant API calls
  private dataCache: { [key: string]: (FeatureCardData | ModelCardData)[] } = {};

  ngOnInit(): void {
    this.switchTab('datasets');
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /**
   * Update itemsToShow based on screen size
   * Desktop: 3 cards, Medium: 2 cards, Mobile: 1 card
   */
  @HostListener('window:resize')
  updateItemsToShow(): void {
    const width = window.innerWidth;
    if (width < 768) {
      // Mobile: 1 card per page
      this.itemsToShow = 1;
    } else if (width < 992) {
      // Medium devices: 2 cards per page
      this.itemsToShow = 2;
    } else {
      // Desktop: 3 cards per page
      this.itemsToShow = 3;
    }
  }

  async switchTab(tabId: string): Promise<void> {
    this.activeTab = tabId;
    this.currentTab = this.tabs.find(tab => tab.id === tabId) || this.tabs[0];

    // Check if data is already cached
    if (this.dataCache[tabId]) {
      this.currentCards = this.dataCache[tabId];
      return;
    }

    // Load data from API
    await this.loadTabData(tabId);
  }

  /**
   * Load data for a specific tab from the API
   */
  private async loadTabData(tabId: string): Promise<void> {
    try {
      this.isLoading = true;
      const currentTab = this.tabs.find(tab => tab.id === tabId);

      if (!currentTab) {
        console.warn(`Tab with id ${tabId} not found`);
        this.currentCards = this.getFallbackData(tabId);
        return;
      }

      // Call the API with pagination
      const response = await this.catalogueApiService.searchAllAssets(
        '', // No search query - fetch all
        [currentTab.assetType], // Filter by asset type
        [], // No custom filter - get all fields
        1, // page
        6  // size
      );

      // Parse and map the response
      const items = this.extractItemsFromResponse(response);

      // Use different mapping based on tab type
      if (tabId === 'ai-models') {
        this.currentCards = items.map(item => this.mapApiItemToModelCard(item));
      } else {
        this.currentCards = items.map(item => this.mapApiItemToFeatureCard(item));
      }

      // Cache the data
      this.dataCache[tabId] = this.currentCards;

    } catch (error) {
      console.error(`Error loading data for tab ${tabId}:`, error);
      // Fallback to mock data on error
      this.currentCards = this.getFallbackData(tabId);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Extract items array from API response
   */
  private extractItemsFromResponse(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    } else if (response?.result && Array.isArray(response.result)) {
      return response.result;
    } else if (response?.data && Array.isArray(response.data)) {
      return response.data;
    } else if (response?.items && Array.isArray(response.items)) {
      return response.items;
    }
    return [];
  }

  /**
   * Map API item to FeatureCardData interface
   */
  private mapApiItemToFeatureCard(item: any): FeatureCardData {
    // Extract basic fields
    const id = item.id || '';
    const title = item.label || item.name || 'Untitled';
    const organization = item.provider || item.organisation || item.organization || item.provider_name || '';
    // Short description: prefer shortDescription, summary, then description (one line with ellipsis in UI)
    const rawDesc = item.shortDescription ?? item.short_description ?? item.summary ?? item.description ?? '';
    const shortDescription = typeof rawDesc === 'string' && rawDesc.trim()
      ? (rawDesc.length > 120 ? rawDesc.trim().slice(0, 120) + '…' : rawDesc.trim())
      : undefined;

    const { resourceServerNames, queryTypes } = extractDatasetResourceDisplayTags(item);

    // Extract actual tags from API
    const apiTags: string[] = [];
    if (item.tags && Array.isArray(item.tags)) {
      apiTags.push(...item.tags);
    }

    // Extract percentage (data readiness or quality score); normalize string to number
    const rawPct = item.percentage ?? item.dataReadiness ?? item.quality ?? item.dataQuality;
    const pctNum = rawPct != null && rawPct !== ''
      ? (typeof rawPct === 'number' ? rawPct : parseFloat(String(rawPct).replace(/%/g, '')))
      : undefined;
    const percentage = (pctNum != null && !Number.isNaN(pctNum)) ? pctNum : undefined;

    // Extract file format
    const fileType = this.extractFileType(item);
    const date = this.formatService.formatDateForCard(item.itemCreatedAt || item.createdAt || new Date());

    // Extract badge/status using helper service
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);

    // Extract resource count if available
    const resourceCount = item.resourceCount || item.resources?.length || undefined;

    // Check if item is new (created within last 7 days)
    const isNew = this.isRecentItem(item.itemCreatedAt || item.createdAt);

    // Build link based on asset type
    const link = this.buildAssetLink(item);

    // Extract metrics (likes, views, downloads) for activity display
    const metricsData = item.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item.likeCount ?? item.likes ?? item.like_count ?? 0,
      downloads: metricsData.downloads ?? item.downloadCount ?? item.downloads ?? item.download_count ?? 0,
      views: metricsData.views ?? item.viewCount ?? item.views ?? item.view_count ?? 0,
    };

    return {
      id,
      title,
      shortDescription,
      organization,
      tags: resourceServerNames.slice(0, 3),
      serverAccessTypes: queryTypes.length > 0 ? queryTypes : undefined,
      apiTags: apiTags, // Actual tags from API
      percentage,
      fileType,
      date,
      badge,
      badgeColor,
      resourceCount,
      isNew,
      link,
      metrics,
    };
  }

  /**
   * Extract file type from item
   */
  private extractFileType(item: any): string {
    if (item.fileFormat) return item.fileFormat;
    if (item.fileType) return item.fileType;
    if (item.format) return item.format;
    if (item.resourceType) {
      const resourceType = Array.isArray(item.resourceType) ? item.resourceType[0] : item.resourceType;
      return resourceType;
    }
    return '';
  }

  /**
   * Check if item was created within last 7 days
   */
  private isRecentItem(dateString: string | Date): boolean {
    if (!dateString) return false;
    const itemDate = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return itemDate >= weekAgo;
  }

  /**
   * Build asset detail link based on type
   */
  private buildAssetLink(item: any): string {
    const id = item.id || '';
    const type = item.type?.[0] || item.type || '';

    if (type.includes('DataBank')) {
      return `/dataset/${id}`;
    } else if (type.includes('AiModel')) {
      return `/model/${id}`;
    } else if (type.includes('Apps') || type.includes('UseCase')) {
      return `/usecase/${id}`;
    }

    return `/asset/${id}`;
  }

  /**
   * Map API item to ModelCardData interface
   */
  private mapApiItemToModelCard(item: any): ModelCardData {
    // Extract basic fields
    const id = item.id || '';
    const title = item.label || item.name || 'Untitled';
    const organization = item.provider || item.organisation || item.organization || item.provider_name || '';
    const rawDesc = item.shortDescription ?? item.short_description ?? item.description ?? '';
    const shortDescription = typeof rawDesc === 'string' && rawDesc.trim()
      ? (rawDesc.length > 120 ? rawDesc.trim().slice(0, 120) + '…' : rawDesc.trim())
      : undefined;

    // Extract tags from API
    const apiTags: string[] = [];
    if (item.tags && Array.isArray(item.tags)) {
      apiTags.push(...item.tags);
    }

    // Extract industry (for user icon display)
    let industry = '';
    if (item.industry) {
      if (Array.isArray(item.industry)) {
        industry = item.industry.filter((v: any) => v !== null && v !== undefined && v !== '').join(', ');
      } else if (typeof item.industry === 'string' && item.industry.trim() !== '') {
        industry = item.industry.trim();
      }
    }

    // Extract model type
    const modelType = item.modelType || '';

    // Extract code language
    const language = item.codeLanguage || '';

    const fileFormat = item.fileFormat || item.format || '';

    // Extract date using model card format
    const date = this.formatService.formatDateForModelCard(item.itemCreatedAt || item.createdAt || new Date());

    // Extract badge/status using helper service
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);

    // Build link based on asset type
    const link = this.buildAssetLink(item);

    // Extract metrics data from metrics object - always include metrics object
    const metricsData = item.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item.likeCount ?? item.likes ?? item.like_count ?? 0,
      downloads: metricsData.downloads ?? item.downloadCount ?? item.downloads ?? item.download_count ?? 0,
      files: item.fileCount || item.files || item.file_count || 0,
      views: metricsData.views ?? item.viewCount ?? item.views ?? item.view_count ?? 0,
      dislikes: metricsData.dislikes ?? 0
    };

    return {
      id,
      title,
      organization,
      shortDescription,
      industry, // Industry for user icon display
      tags: apiTags.slice(0, 3), // Use actual tags from API
      modelType,
      language,
      fileFormat: fileFormat || undefined,
      date,
      badge,
      badgeColor,
      link,
      metrics: metrics // Always include metrics object
    };
  }

  /**
   * Get fallback mock data for a tab
   */
  private getFallbackData(tabId: string): (FeatureCardData | ModelCardData)[] {
    switch (tabId) {
      case 'datasets':
        return this.datasetsCards;
      case 'ai-models':
        return this.aiModelsCards;
      default:
        return this.datasetsCards;
    }
  }

  onSlideChange(index: number): void {
    console.log('Slide changed to:', index);
  }

  /**
   * Navigate to the view all page for the current tab
   */
  onViewAll(): void {
    if (this.currentTab && this.currentTab.viewAllLink) {
      this.router.navigate([this.currentTab.viewAllLink]);
    }
  }
}

